import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
} from '@eluve/components';

import { GROUP_DIMS, GROUP_DIM_LABELS, GroupDim } from './groupDims';

const SelectedChip: React.FC<{
  dim: GroupDim;
  index: number;
  onRemove: (dim: GroupDim) => void;
}> = ({ dim, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dim });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex cursor-grab items-center gap-1.5 rounded-full bg-bg-tertiary py-1 pr-2 pl-2.5"
    >
      <Icon name="GripVertical" size="xs" className="text-text-tertiary" />
      <Text size="xs" color="tertiary">
        {index + 1}.
      </Text>
      <Text size="sm" weight="semibold">
        {GROUP_DIM_LABELS[dim]}
      </Text>
      <button
        aria-label={`Remove ${GROUP_DIM_LABELS[dim]} grouping`}
        onClick={() => onRemove(dim)}
        onPointerDown={(event) => event.stopPropagation()}
        className="ml-auto flex items-center rounded-full p-0.5 hover:bg-bg-secondary"
      >
        <Icon name="X" size="xs" />
      </button>
    </div>
  );
};

/**
 * Compact toolbar button. The popover holds the selected dimensions (drag
 * to reorder — the order defines the drill-down hierarchy, with providers
 * always forming the final level) and the remaining dimensions to add; the
 * trigger shows a count badge when any grouping is applied.
 */
export const GroupByBar: React.FC<{
  value: GroupDim[];
  onChange: (value: GroupDim[]) => void;
}> = ({ value, onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const available = GROUP_DIMS.filter((dim) => !value.includes(dim));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const from = value.indexOf(active.id as GroupDim);
    const to = value.indexOf(over.id as GroupDim);
    if (from >= 0 && to >= 0) {
      onChange(arrayMove(value, from, to));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" aria-label="Group by">
          <Icon name="Layers" size="sm" />
          <Text size="sm" color="tertiary">
            Group by
          </Text>
          <Text size="sm" weight="semibold">
            {value.length
              ? value.map((dim) => GROUP_DIM_LABELS[dim]).join(', then by ')
              : 'None'}
          </Text>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <Stack gap={2}>
          {value.length > 0 && (
            <Stack gap={1.5}>
              <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={value}
                  strategy={verticalListSortingStrategy}
                >
                  {value.map((dim, index) => (
                    <SelectedChip
                      key={dim}
                      dim={dim}
                      index={index}
                      onRemove={(removed) =>
                        onChange(value.filter((entry) => entry !== removed))
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <Text size="xs" color="tertiary">
                Drag to reorder · Expand rows to drill down
              </Text>
            </Stack>
          )}
          {available.length > 0 && (
            <Stack gap={1}>
              {value.length > 0 && (
                <Text size="xs" color="tertiary">
                  Add grouping
                </Text>
              )}
              {available.map((dim) => (
                <Button
                  key={dim}
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  onClick={() => onChange([...value, dim])}
                >
                  <Icon name="Plus" size="xs" />
                  {GROUP_DIM_LABELS[dim]}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
